import { ThemeEvents } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} EmpDescuentoRefs
 * @property {HTMLElement} [pillContainer] - The element holding the rendered pill
 */

/**
 * The discount pill rendered under the price (blocks/emp-descuento.liquid).
 *
 * All the money math lives in Liquid, so this component never recomputes
 * anything: on a variant update it takes the re-rendered pill out of the
 * response document and swaps its container, the same way product-price.js
 * refreshes the price. That keeps a single source of truth for the percentage
 * and its rounding.
 *
 * @extends {Component<EmpDescuentoRefs>}
 */
class EmpDescuento extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.closest('.shopify-section, dialog')?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.closest('.shopify-section, dialog')?.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  /**
   * Replaces the pill with the version rendered for the newly selected variant.
   * @param {CustomEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      // A picker for a different product fired this event. Leave our pill alone.
      return;
    }

    const newPill = event.detail.data.html.querySelector(`emp-descuento[data-block-id="${this.dataset.blockId}"]`);
    if (!newPill) return;

    const newContainer = newPill.querySelector('[ref="pillContainer"]');
    const { pillContainer } = this.refs;
    if (newContainer && pillContainer) {
      pillContainer.replaceWith(newContainer);
    }

    // Variants without a discount render an empty, hidden pill. Mirror the
    // server's decision both ways so the pill hides and reappears as the
    // shopper moves between variants.
    this.hidden = newPill.hasAttribute('hidden');
  };
}

if (!customElements.get('emp-descuento')) {
  customElements.define('emp-descuento', EmpDescuento);
}
